import { calculateExpression } from "@editor/items/expression/expressionFunctions";
import type { ItemStoreState } from "@editor/items/ItemStore";
import type { StoreItem } from "@editor/items/StoreItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { SceneNodeItem } from "@editor/items/scene/SceneNodeItem";
import type { SceneItem } from "@editor/items/scene/SceneItem";

type StoreState = {
  _meta?: {
    appVersion: string;
    savedAt?: number;
    locale?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [storeId: string]: ItemStoreState | { [key: string]: any } | undefined;
};

export function vensimToJSON(vensim: string): StoreState {
  const data: StoreState = {};
  console.log("⬇️ SOURCE");
  const lines = vensim.match(/[^\r\n]+/g)?.values() || [];
  console.log(lines);

  type VensimStock = {
    type?: string;
    uid?: string;
    id?: number;
    title: string;
    description: string;
    valueExpression?: string;
    value?: number;
    unit: string;
    minValue?: number;
    maxValue?: number;
    increment?: number;
    x?: number;
    y?: number;
  };

  const stocks: { [title: string]: VensimStock } = {};

  let section = "equations";
  let currentStock: VensimStock | null = null;
  let subSection: "expression" | "unit" | "comment" = "expression";

  type VensimView = {
    title: string;
    items: VensimStock[];
  };
  const views: { [name: string]: VensimView } = {};
  let currentView: VensimView | null = null;

  lineloop: for (const line of lines) {
    if (line.startsWith("{UTF")) continue;

    if (section == "equations") {
      if (currentStock != null) {
        if (subSection == "expression") {
          if (line.startsWith("\t~\t")) {
            subSection = "unit";
          } else {
            currentStock.valueExpression += line;
          }
        }
        if (subSection == "unit") {
          // substring lines start with \t\~\t
          const lineContent = line.substring(3).trim();
          const rangeInfo = lineContent.match(/\[(.*)\]/);
          if (rangeInfo) {
            const [minValue, maxValue, increment] = rangeInfo[1].split(",");
            currentStock.minValue = parseFloat(minValue);
            currentStock.maxValue = parseFloat(maxValue);
            currentStock.increment = parseFloat(increment);
            currentStock.unit = lineContent.replace(rangeInfo[0], "").trim();
          } else {
            currentStock.unit = lineContent;
          }
          subSection = "comment";
        }

        if (line.endsWith("|")) {
          stocks[currentStock.title] = currentStock;
          currentStock.title = currentStock.title.replaceAll(/\"/g, "");
          currentStock.valueExpression = (
            currentStock.valueExpression || ""
          ).replaceAll(/\t/g, "");
          currentStock = null;
          subSection = "expression";
        }
      } else {
        if (line.startsWith("***")) {
          section = "unknown";
        } else if (line.startsWith(`\\\\\\---///`)) {
          section = "sketch";
        } else {
          const lastIndexOfEquals = line.lastIndexOf("=");
          const title = line.substring(0, lastIndexOfEquals);
          const expression = line.substring(lastIndexOfEquals + 1);
          currentStock = {
            title: title.trim(),
            description: "",
            unit: "",
            valueExpression: expression.trim()
          };
        }
      }
    }

    if (line.startsWith("\\\\\\---/// ")) {
      section = "sketch";
      if (currentView) {
        views[currentView.title] = currentView;
      }
      currentView = {
        title: "",
        items: []
      };
    } else if (section == "sketch") {
      if (line.startsWith("///---\\\\\\")) {
        currentView = null;
        section = "unknown";

        continue lineloop;
      }

      if (currentView) {
        if (line.startsWith("*")) {
          currentView.title = line.substring(1).trim();
          views[currentView.title] = currentView;
        }

        // parse a stock position
        // e.g.: 10,9,"Births Mp/y",856,244,38,11,40,131,0,0,-1,0,0,0,0,0,0,0,0,0
        const elementPos = line.match(
          /(-?[\d]+),(-?[\d]+),(.*),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+|-?[\d]+--?[\d]+--?[\d]+),(-?[\d]+|-?[\d]+--?[\d]+--?[\d]+),(-?[\d]+|\|-?[\d]+\|[B]*\|-?[\d]+--?[\d]+--?[\d]+),(-?[\d]+),(-?[\d]+),(-?[\d]+)/
        );
        if (elementPos) {
          const title = elementPos[3];
          const x = parseInt(elementPos[4]);
          const y = parseInt(elementPos[5]);

          const stock = stocks[title];
          if (stock) {
            stock.x = x;
            stock.y = y;

            currentView.items.push(stock);
          } else {
            console.warn("Stock not found", title);
          }
        }
      }
    }
  }

  console.log(stocks, views);

  const stocksWithoutPositions = Object.values(stocks).filter(
    (stock) => stock.x == null || stock.y == null
  );
  console.log("stocks without positions", stocksWithoutPositions);
  
  // position stocks without positions
  let positionlessI = 0;
  for (const stock of stocksWithoutPositions) {
    stock.x = -200;
    stock.y = positionlessI * 200;
    positionlessI += 1;
  }


  const resourceStore: ItemStoreState<
    StoreItem & VensimStock & Partial<ResourceItem>
  > = {
    type: "objectstore",
    itemStructure: {
      type: "resource",
      title: "",
      description: "",
      unit: "",
      minValue: 0,
      maxValue: 0,
      value: 0,
      id: 0,
      uid: "resource_0",
      createdAt: 0
    },
    items: {},
    count: 0,
    order: []
  };

  const indexStore: ItemStoreState<
    StoreItem & VensimStock & Partial<IndexItem>
  > = {
    type: "objectstore",
    itemStructure: {
      type: "index",
      title: "",
      description: "",
      unit: "",

      id: 0,
      uid: "index_0",
      createdAt: 0
    },
    items: {},
    order: [],
    count: 0
  };

  const pureNumberRegex = /^-?(\d*\.){0,1}\d+$/;
  const isIntegral = /^INTEG(.*)$/;
  const isSmooth = /^SMOOTH(.*)$/;
  const isSmoothi = /^SMOOTHI(.*)$/;
  for (const stock of Object.values(stocks)) {
    const valueExpression = stock.valueExpression || "";

    if (pureNumberRegex.test(valueExpression)) {
      // is a constant
      const id = resourceStore.count++;
      const uid = `resource_${id}`;

      stock.type = "resource";
      stock.uid = uid;
      stock.id = id;

      stock.value = parseFloat(valueExpression);
      delete stock.valueExpression;

      resourceStore.items[id] = stock as StoreItem &
        VensimStock &
        Partial<ResourceItem>;
      resourceStore.order.push(id);
    } else if (isIntegral.test(valueExpression) || isSmooth.test(valueExpression) || isSmoothi.test(valueExpression)){
      const id = resourceStore.count++;
      const uid = `resource_${id}`;

      stock.type = "resource";
      stock.uid = uid;
      stock.id = id;

      resourceStore.items[id] = stock as StoreItem &
        VensimStock &
        Partial<ResourceItem>;
      resourceStore.order.push(id);
    } else {
      // is an index
      const id = indexStore.count++;
      const uid = `index_${id}`;

      stock.type = "index";
      stock.uid = uid;
      stock.id = id;

      delete stock.minValue;
      delete stock.maxValue;

      indexStore.items[id] = stock as StoreItem &
        VensimStock &
        Partial<IndexItem>;
      indexStore.order.push(id);
    }
  }

  // convert expressions to simcision format
  for (const stock of Object.values(stocks)) {
    // remove "1" unit
    if (stock.unit == "1") stock.unit = "";

    let expression = stock.valueExpression || "";
    if (!expression) continue;

    if (expression.includes("DELAY N")) console.log(expression);

    const resourcesByTitleLength = Object.values(resourceStore.items).sort((a, b) => b.title.length - a.title.length);
    const indicesByTitleLength = Object.values(indexStore.items).sort((a, b) => b.title.length - a.title.length);

    if (stock.uid == "resource_325") {
      console.log("⚠️⚠️", {
        ...stock
      });
    }

    // replace stock references
    for (const resource of resourcesByTitleLength) {
      const mark = `resource_${resource.id}`;
      expression = expression
        .replaceAll(`"${resource.title}"`, mark)
        .replaceAll(resource.title, mark);
    }
    for (const index of indicesByTitleLength) {
      const mark = `index_${index.id}`;
      expression = expression
        .replaceAll(`"${index.title}"`, mark)
        .replaceAll(index.title, mark);
    }

    // replace vensim separators with simcision separators
    expression = expression.replaceAll(",", ";");

    // replace vensim function calls with simcision function calls
    expression = expression.replaceAll(`IF THEN ELSE`, "ifelse");
    expression = expression.replaceAll("EXP", "exp");
    expression = expression.replaceAll("LOG", "log"); // TODO: FIX this: the log function in vensim is not the same as in JS.
    expression = expression.replaceAll("LN", "log");
    expression = expression.replaceAll("SQRT", "sqrt");
    expression = expression.replaceAll("SIN", "sin");
    expression = expression.replaceAll("COS", "cos");
    expression = expression.replaceAll("ABS", "abs");
    expression = expression.replaceAll("MIN", "min");
    expression = expression.replaceAll("MAX", "max");
    expression = expression.replaceAll("MOD", "mod");
    expression = replaceFunction(expression, "RAMP", (_, args) => {
      // TODO: should not be "year", but the unit used in the model.
      // TODO: Check if this is correct...
      return `ifelse($\{year} >= ${args[0]} & $\{year} <= (${args[0]} + ${args[1]}); $\{year} - ${args[0]}; 0)`;
    });
    expression = replaceFunction(expression, "PULSE", (_, args) => {
      console.log("PULSE", _, args);
      // TODO: should not be "year", but the unit used in the model.
      // Vensim has some time offsets too, we do not have that.
      return `ifelse($\{year} >= ${args[0]} & $\{year} <= (${args[0]} + ${args[1]}); 1; 0)`;
    });
    expression = replaceFunction(expression, "DELAY N", (_, args) => {
      // TODO: I don't understand vensims DELAY N function. 
      // Try understanding and integrating it.
      // DELAY N (value, delay time, initial value, order)
      return `delay(${args[0]}; ${args[1]})`
    });

    expression = replaceFunction(expression, "WITH LOOKUP", (_, args) => {
      // This is not currently possible as-is in simcision.
      // TODO: implement.
      return "0";
    });
    expression = replaceFunction(expression, "RANDOM PINK NOISE", (_, args) => {
      // simcision does not currently have this function. To implement.
      // TODO!
      return "0";
    });
    expression = replaceFunction(expression, "STEP", (_, args) => {
      // TODO
      return "0";
    });

    expression = expression.replaceAll("Time", "${year}");

    // untranslated:
    // [✓] INTEG(rate, initial)
    // [✓] SMOOTH(input, delay)
    // [✓] SMOOTHI(input, delay, initial)
    // [✓] PULSE
    // [✓] LN
    // [ ] WITH LOOKUP(a, b, c, d, e, f, g, h, i, j, k, l, m, n, o, p, q)
    // [ ] DELAY N
    // [ ] RANDOM PINK NOISE

    stock.valueExpression = expression;
  }

  // create flow from INTEG functions
  const flowStore: ItemStoreState<StoreItem & Partial<FlowItem>> = {
    type: "objectstore",
    items: {},
    itemStructure: {
      type: "flow",
      id: 0,
      uid: "flow_0",
      valueExpression: "",
      createdAt: 0
    },
    order: [],
    count: 0
  };
  const flowBranchStore: ItemStoreState<StoreItem & Partial<FlowBranchItem>> = {
    type: "objectstore",
    items: {},
    itemStructure: {
      type: "flowBranch",
      id: 0,
      uid: "flowBranch_0",
      createdAt: 0
    },
    order: [],
    count: 0
  };

  for (const stock of Object.values(stocks)) {
    let expression = stock.valueExpression || "";

    if (expression.startsWith("INTEG")) {
      let expressionContent = expression.substring(7, expression.length - 1);
      
      // split integral function and initial value:
      let [newExpression, initial] = splitLastParameter(expressionContent);

      expression = newExpression;
      // console.log("INTEGRAL", newExpression, initial);

      if (!expression.startsWith("-")) {
        expression = "+" + expression;
      }

      // extract flows from reference additions/subtractions
      let extractedFlowsCount = 0;
      let matchesOrder = [
        /([+-])[\s]*(@\{[etri]\d+\}[/*][+-]*@\{[etri]\d+\})/,
        /([-+])[\s]*(@\{[etri]\d+\})/
      ];
      let match;
      do {
        match = expression.match(matchesOrder[0]);

        if (!match) {
          matchesOrder.shift();
          continue;
        }
        
        const matchedString = match[0];
        const matchedSign = match[1];
        const matchedReference = match[2];
        // console.log("extract", matchedSign, matchedReference);
        const flowExpression = matchedReference.replaceAll("\\", "");
        const index = match.index || 0;

        // remove from expression
        expression =
          expression.substring(0, index) +
          expression.substring(index + matchedString.length);
        createFlow(
          flowStore,
          flowBranchStore,
          flowExpression,
          { type: "resource", id: stock.id },
          matchedSign == "+" ? "sink" : "source"
        );
        extractedFlowsCount++;
      } while (match != null || matchesOrder.length > 0);

      expression = expression.trim();
      
      console.log("extracted", extractedFlowsCount, "flows");
      if (expression != "") {
        console.warn("rest expression:", expression);
      }

      stock.valueExpression = initial;
    }
    else if (expression.startsWith("SMOOTHI")) {
      let initial = "";

      const flowExpression = replaceFunction(expression, "SMOOTHI", (_, args) => {
        const valueExpression = args[0];
        const delay = args[1];
        initial = args[args.length - 1];

        let smoothExpression = `(${valueExpression}-resource_${stock.id}) / ${delay}`;
        return smoothExpression;
      });

      createFlow(flowStore, flowBranchStore, flowExpression.replaceAll("\\", ""), { type: "resource", id: stock.id }, "sink");

      stock.valueExpression = initial;
    } else if (expression.startsWith("SMOOTH")) {
      let initial = "";

      const flowExpression = replaceFunction(expression, "SMOOTH", (_, args) => {
        const valueExpression = args[0];
        const delay = args[1];
        initial = args[args.length - 1];

        let smoothExpression = `(${valueExpression}-resource_${stock.id}) / ${delay}`;
        return smoothExpression;
      });
      
      createFlow(flowStore, flowBranchStore, flowExpression.replaceAll("\\", ""), { type: "resource", id: stock.id }, "sink");

      stock.valueExpression = initial;
    }
  }

  for (const stock of Object.values(stocks)) {
    if (stock.valueExpression){
      stock.valueExpression = stock.valueExpression?.replaceAll("\\", "");
    }

    stock as StoreItem & VensimStock & Partial<ResourceItem>;

    if (stock.value != null) {
      if (stock.minValue == null) stock.minValue = stock.value;
      if (stock.maxValue == null) stock.maxValue = stock.value;
    }
  }

  // Calculate all resource initial values
  let tries = 20;
  
  const resourcesToCalculate = Object.values(resourceStore.items).filter(
    (resource) => resource.valueExpression
  );
  for (const resource of resourcesToCalculate) {
    try {
      const calculatedValue = calculateExpression(resource.valueExpression || "", {
        resources: resourceStore.items as { [key: string]: ResourceItem },
        indices: indexStore.items as { [key: string]: IndexItem }
      });
      if (calculatedValue.value == null || isNaN(Number(calculatedValue.value))) {
        // try again later?
        if (tries-- > 0) {
          resourcesToCalculate.push(resource);
        }
      } else {
        resource.value = Number(calculatedValue.value);
        delete resource.valueExpression;
      }
    } catch (error) {
      console.warn(error);
    }
  }

  const sceneStore: ItemStoreState<
    StoreItem & Partial<SceneItem>
  > = {
    type: "objectstore",
    itemStructure: {
      type: "scene",
      id: 0,
      uid: "scene_0",
      createdAt: 0,

      title: ""
    },
    items: {},
    order: [],
    count: 0
  };

  // Position all stocks in views
  const nodeStore: ItemStoreState<
    StoreItem & Partial<SceneNodeItem>
  > = {
    type: "objectstore",
    itemStructure: {
      type: "sceneNode",
      id: 0,
      uid: "sceneNode_0",
      createdAt: 0,

      sceneId: 0,
      title: "",
      data: {},

      x: 0,
      y: 0,
      width: 0,
      height: 0
    },
    items: {},
    order: [],
    count: 0
  };

  let viewI = 0;
  const alreadyHandled = new Set<string>();
  for (const view of Object.values(views).reverse()) {
    const scene: SceneItem = {
      type: "scene",
      id: sceneStore.count++,
      uid: `scene_${sceneStore.count}`,
      createdAt: Date.now(),
      title: view.title,
      backgroundColor: "#ffffff",
      backgroundImageId: null,
      indexConnectionsVisible: true,
      linkRelationsDisplay: "visible",
      flowRelationsDisplay: "visible",
      elementsDisplayMode: "value",
      footerAnchored: false,
      translateX: 0,
      translateY: 0,
      scale: 0.6,
      editModeOnly: false,
      templateId: null,
      areasVisible: true,
      placeholdersVisible: true,
    };
    sceneStore.items[scene.id] = scene;
    sceneStore.order.push(scene.id);

    const layerNode: SceneNodeItem = {
      type: "sceneNode" as const,
      id: nodeStore.count++,
      uid: `sceneNode_${nodeStore.count}` as const,
      createdAt: Date.now(),
      x: 0,
      y: 0,
      width: 1000,
      height: 800,
      rotation: 0,
      opacity: 1,
      visible: true,
      locked: true,
      title: view.title + " (layer)",
      sceneId: scene.id,
      parentSceneNodeUid: null,
      order: 0,
      dataType: "layer",
      data: {
        layerType: "scene"
      }
    };
    nodeStore.items[layerNode.id] = layerNode;
    nodeStore.order.push(layerNode.id);

    let order = 0;
    for (const stock of view.items) {
      if (stock.x == null || stock.y == null) continue;
      if (alreadyHandled.has(stock.title)) continue;
      stock.x *= 2;
      stock.y *= 2;

      const node: SceneNodeItem = {
        type: "sceneNode" as const,
        id: nodeStore.count++,
        uid: `sceneNode_${nodeStore.count}` as const,
        createdAt: Date.now(),
        x: stock.x,
        y: stock.y,
        width: 200,
        height: 60,
        rotation: 0,
        opacity: 1,
        visible: true,
        locked: false,
        title: stock.title,
        sceneId: scene.id,
        parentSceneNodeUid: layerNode.uid,
        order: order += 100,
        dataType: "element",
        data: {
          elementUid: stock.type == "resource" ? `resource_${stock.id!}` : `index_${stock.id!}`,
          headlineHidden: false,
          barVisible: true,
          imageId: null,
          valueVisible: true,
          barWidthLimited: true
        }
      };
      nodeStore.items[node.id] = node;
      nodeStore.order.push(node.id);

      // stock.x += viewX;
      alreadyHandled.add(stock.title);
    }
    viewI += 1;
  }

  console.log(indexStore.items, resourceStore.items);

  data.indices = indexStore;
  data.resources = resourceStore;
  data.flows = flowStore;
  data.flowBranches = flowBranchStore;

  console.log("➡️ RESULT", data);
  return data;
}

function createFlow(
  flowStore: ItemStoreState,
  branchStore: ItemStoreState,
  flowExpression: string,
  element: { type: string; id?: number },
  branchType: "source" | "sink" = "source"
) {
  const flowId = flowStore.count++;
  const flow: StoreItem & Partial<FlowItem> = {
    type: "flow",
    id: flowId,
    uid: `flow_${flowId}`,
    valueExpression: flowExpression,
    valueInputMode: "expression",
    createdAt: Date.now()
  };
  flowStore.items[flowId] = flow;
  flowStore.order.push(flowId);

  const branchId = branchStore.count++;
  const branch: StoreItem & Partial<FlowBranchItem> = {
    type: "flowBranch",
    id: branchId,
    uid: `flowBranch_${branchId}`,
    flowId: flowId,
    curveType: "formula",
    formula: branchType == "source" ? "-x" : "x",
    elementId: element.id,
    elementType: element.type as "resource",
    branchType: branchType,
    createdAt: Date.now(),
    curvePoints: [{ id: 0, x: 0, y: 0, bezierOut: { x: 0, y: 0 } }, { id: 1, x: 1, y: 1, bezierIn: { x: 0, y: 0 }}]
  };
  branchStore.items[branchId] = branch;
  branchStore.order.push(branchId);

  // console.log("created flow", flow, branch);

  return { flow, branch };
}

function splitLastParameter(expression: string) {
  let parentheses = 0;
  expression = expression.replaceAll("\\", "");
  for (let i = expression.length; i >= 0; i--) {
    const letter = expression[i];
    if (letter == ")") parentheses++;
    if (letter == "(") parentheses--;
    if (letter == ";" && parentheses == 0) {
      return [expression.substring(0, i), expression.substring(i + 1)];
    }
  }
  return [expression, ""];
}

function replaceFunction(expression: string, functionName: string, getReplacement: (functionString: string, args: string[]) => string) {
  let index = expression.indexOf(functionName);
  while (index > -1) {
    const { args, end } = extractParenthesisContent(expression, index + functionName.length + 1);

    let functionString = expression.substring(index, end);
    let replaced = getReplacement(functionString, args);
    expression = expression.replace(functionString, replaced);
    index = expression.indexOf(functionName);
  }

  return expression;
}

function extractParenthesisContent(expression: string, startIndex: number): { args: string[], end: number } {
  const args: string[] = [];

  let end = expression.length;

  let argIndex = 0;
  let parentheses = 0;
  for (let i = startIndex; i < expression.length; i++) {
    const letter = expression[i];
    if (letter == ")") {
      if (parentheses == 0) {
        end = i + 1;
        break;
      }
      parentheses--;
    }
    else if (letter == "(") {
      parentheses++;
    }
    else if (parentheses == 0 && letter == ";") {
      argIndex += 1;
    } else {
      if (!args[argIndex]) args[argIndex] = "";
      args[argIndex] += letter;
    }
  }

  return {
    args,
    end
  };
}

export function isVensimFile(file: File | Blob): boolean {
  if ("name" in file) {
    return file.name.endsWith(".mdl");
  }
  if (file instanceof Blob) {
    return file.type == "text/plain" || file.type == "application/vnd.vensim";
  }
  return false;
}
